import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { MapPin, Mail, Phone, Shield, Award, ShieldCheck, Facebook, Instagram, Youtube, Twitter, Linkedin } from "lucide-react";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube, twitter: Twitter, linkedin: Linkedin };

export default function Footer() {
  const { settings } = useSettings();
  const social = settings.social_links || {};
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCategories(r.data.filter((c) => !c.parent_id));
    }).catch(() => {});
  }, []);

  const seoLinks = settings.footer_seo_links || [];
  const siteName = settings.site_name || "HyderabadTourism.in";

  return (
    <footer className="mt-20 bg-[#041A1A] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Column 1: Brand & Contact */}
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt={siteName} className="h-11 w-auto object-contain rounded-xl brightness-0 invert" />
              ) : (
                <div className="relative w-11 h-11 rounded-xl overflow-hidden shadow-lg" style={{background: "linear-gradient(135deg, #00B7EB 0%, #008FA4 55%, #006B85 100%)"}}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white drop-shadow" strokeWidth={2.4} />
                  </div>
                </div>
              )}
              <div className="font-display text-xl font-bold tracking-tight">{siteName}</div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              {settings.footer_tagline || `${siteName.split(".")[0]}'s most trusted travel partner since ${settings.trust_badges?.est_year || "2005"}. Tours, hotels, tickets, cabs and adventure — all curated.`}
            </p>
            <div className="flex gap-3 mt-5 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs bg-white/10 rounded-full px-3 py-1.5"><Award className="w-3.5 h-3.5 text-saffron" />Est. {settings.trust_badges?.est_year || "2005"}</div>
              <div className="flex items-center gap-1.5 text-xs bg-white/10 rounded-full px-3 py-1.5"><Shield className="w-3.5 h-3.5 text-saffron" />GST Reg.</div>
              <div className="flex items-center gap-1.5 text-xs bg-white/10 rounded-full px-3 py-1.5"><ShieldCheck className="w-3.5 h-3.5 text-saffron" />Razorpay</div>
            </div>
            <div className="flex gap-2 mt-5" data-testid="footer-social">
              {Object.entries(SOCIAL_ICONS).map(([k, Icon]) => (
                social[k] ? (
                  <a key={k} href={social[k]} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg bg-white/10 hover:bg-saffron flex items-center justify-center transition-colors"
                    data-testid={`footer-social-${k}`}>
                    <Icon className="w-4 h-4" />
                  </a>
                ) : null
              ))}
            </div>
          </div>

          {/* Column 2: Explore (dynamic categories) */}
          <div>
            <h4 className="font-display text-lg mb-4 text-saffron">Explore</h4>
            <ul className="space-y-2 text-sm text-white/80">
              {categories.map((cat) => {
                const to = cat.link_query ? `/search?${cat.link_query}` : `/search?category=${cat.product_category || cat.slug}`;
                return (
                  <li key={cat.slug}><Link to={to} className="hover:text-saffron transition-colors" data-testid={`footer-cat-${cat.slug}`}>{cat.name}</Link></li>
                );
              })}
            </ul>
          </div>

          {/* Column 3: Quick Links (CMS pages + static) */}
          <div>
            <h4 className="font-display text-lg mb-4 text-saffron">Quick Links</h4>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/p/about-us" className="hover:text-saffron transition-colors" data-testid="footer-about">About Us</Link></li>
              <li><Link to="/p/contact-us" className="hover:text-saffron transition-colors" data-testid="footer-contact">Contact Us</Link></li>
              <li><Link to="/blog" className="hover:text-saffron transition-colors" data-testid="footer-blog">Travel Blog</Link></li>
              <li><Link to="/faq" className="hover:text-saffron transition-colors" data-testid="footer-faq">FAQ</Link></li>
              <li><Link to="/enquire" className="hover:text-saffron transition-colors" data-testid="footer-enquire">Enquire Now</Link></li>
              <li><Link to="/p/terms-and-conditions" className="hover:text-saffron transition-colors" data-testid="footer-terms">Terms & Conditions</Link></li>
              <li><Link to="/p/privacy-policy" className="hover:text-saffron transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/p/refund-policy" className="hover:text-saffron transition-colors" data-testid="footer-refund">Refund Policy</Link></li>
            </ul>
          </div>

          {/* Column 4: Get in Touch + SEO Links */}
          <div>
            <h4 className="font-display text-lg mb-4 text-saffron">Get in Touch</h4>
            <ul className="space-y-3 text-sm text-white/80">
              {settings.contact_address && (
                <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-saffron shrink-0" /> {settings.contact_address}</li>
              )}
              {settings.contact_phone && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-saffron" />
                  <a href={`tel:${settings.contact_phone.replace(/\s/g, "")}`} className="hover:text-saffron transition-colors">{settings.contact_phone}</a>
                </li>
              )}
              {settings.contact_email && (
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-saffron" />
                  <a href={`mailto:${settings.contact_email}`} className="hover:text-saffron transition-colors">{settings.contact_email}</a>
                </li>
              )}
            </ul>

            {seoLinks.length > 0 && (
              <div className="mt-6">
                <h4 className="font-display text-lg mb-3 text-saffron">Popular</h4>
                <ul className="space-y-1.5 text-sm text-white/80">
                  {seoLinks.filter((l) => l.label && l.url).map((link, i) => (
                    <li key={i}>
                      <Link to={link.url} className="hover:text-saffron transition-colors" data-testid={`footer-seo-${i}`}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Partner / Licence logos strip */}
        {settings.partner_logos && settings.partner_logos.length > 0 && (
          <div className="border-t border-white/10 mt-10 pt-6" data-testid="partner-logos-strip">
            <div className="flex items-center gap-6 flex-wrap justify-center">
              {settings.partner_logos.map((p, i) => (
                <a key={i} href={p.link || "#"} target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity" title={p.name || ""}>
                  {p.image_url ? (
                    <img src={p.image_url.startsWith("/api/") ? `${window.location.origin}${p.image_url}` : p.image_url} alt={p.name || "Partner"} className="h-10 max-w-[120px] object-contain brightness-0 invert"
                      onError={(e) => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "inline-block"; }} />
                  ) : null}
                  <span className="text-xs text-white/60 font-semibold border border-white/20 rounded px-3 py-1.5" style={{ display: p.image_url ? "none" : "inline-block" }}>{p.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-white/10 mt-6 pt-6 flex flex-col md:flex-row justify-between gap-2 text-xs text-white/50">
          <div>© {settings.trust_badges?.est_year || "2005"} - {new Date().getFullYear()} {siteName} — All rights reserved.</div>
          <div>
            {settings.gstin && <span>GSTIN: {settings.gstin}</span>}
            {settings.gstin && settings.iata && <span> · </span>}
            {settings.iata && <span>{settings.iata}</span>}
          </div>
        </div>
      </div>
    </footer>
  );
}
